import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface VolcesSeedream40Params {
  /**
   * 提示词，必填，不超过300个中文字符或600个英文单词
   */
  prompt: string;
  /**
   * 参考图像，支持JPEG或PNG格式的Base64编码或可访问的URL
   */
  image?: string | string[];
  /**
   * 连续图像生成模式，默认值为disabled
   * @default "disabled"
   * @values auto | disabled
   */
  sequential_image_generation?: 'auto' | 'disabled';
  /**
   * 连续图像生成选项，仅当sequential_image_generation为auto时生效
   */
  sequential_image_generation_options?: {
    max_images: number;
  };
  /**
   * 是否启用流式输出，默认值为false
   * @default false
   */
  stream?: boolean;
  /**
   * 响应格式，默认值为url
   * @default "url"
   * @values url | b64_json
   */
  response_format?: 'url' | 'b64_json';
  /**
   * 是否添加水印，默认值为false
   * @default false
   */
  watermark?: boolean;
  /**
   * 图片尺寸，支持分辨率选项(1K, 2K, 4K)或像素尺寸(如: 2048x2048)
   */
  size?: string;
  /**
   * 提示词优化选项
   */
  optimize_prompt_options?: {
    /**
     * 提示词优化模式
     * @values standard | fast
     */
    mode?: 'standard' | 'fast';
  };
}

export interface VolcesSeedream40Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seedream_4_0
 *
 * <p>通过火山引擎Seedream 4.0 API创建图像生成任务。<a href="https://www.volcengine.com/docs/82379/1541523">API Documentation</a></p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeedream40 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeedream40({
 *   // 参数见 VolcesSeedream40Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeedream40Params 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeedream40(
  params: VolcesSeedream40Params,
): Promise<VolcesSeedream40Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seedream_4_0',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeedream40Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'image');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
